import { useMutation } from "@apollo/client";
import { Alert, Box, Button, Stack, TextField } from "@mui/material";
import type { ChangeEvent, FormEvent } from "react";
import { useCallback, useState } from "react";
import { useTranslate } from "react-admin";

import {
  SendMarketingTestEmailDocument,
  type SendMarketingTestEmailMutation,
  type SendMarketingTestEmailMutationVariables,
} from "../generated/graphql";
import { captureException } from "../lib/errorHandling";
import { marketingDeliveryErrorKey, marketingTestSendResultKey } from "../lib/marketingDelivery";
import { space } from "../theme/designTokens";
import { SettingsSection } from "./SettingsSection";

type MarketingTestSendProps = {
  recipientsRestricted: boolean;
  allowedRecipients: string[];
};

type Outcome = { severity: "success" | "error"; message: string };

export const MarketingTestSend = ({ recipientsRestricted, allowedRecipients }: MarketingTestSendProps) => {
  const translate = useTranslate();
  const [to, setTo] = useState("");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [send, { loading: sending }] = useMutation<
    SendMarketingTestEmailMutation,
    SendMarketingTestEmailMutationVariables
  >(SendMarketingTestEmailDocument);

  const handleTo = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTo(event.target.value);
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setOutcome(null);
      try {
        const { data } = await send({ variables: { input: { to: to.trim() } } });
        const result = data?.sendMarketingTestEmail;
        if (result?.ok) {
          setOutcome({
            severity: "success",
            message: translate("marketingDelivery.testSend.accepted", { id: result.providerMessageId }),
          });
        } else {
          const code = result?.errorCode ?? "";
          setOutcome({
            severity: "error",
            message: translate(marketingTestSendResultKey(code), { code }),
          });
        }
      } catch (sendError) {
        const key = marketingDeliveryErrorKey(sendError);
        if (key === null) captureException(sendError, "marketing/delivery: test send");
        setOutcome({ severity: "error", message: translate(key ?? "marketingDelivery.testSend.sendError") });
      }
    },
    [send, to, translate]
  );

  const canSend = !sending && to.trim() !== "";

  return (
    <Box data-testid="marketing-test-send" component="form" noValidate onSubmit={handleSubmit}>
      <SettingsSection title={translate("marketingDelivery.testSend.title")}>
        {recipientsRestricted && (
          <Alert severity="info">
            {allowedRecipients.length > 0
              ? translate("marketingDelivery.testSend.restricted", { recipients: allowedRecipients.join(", ") })
              : translate("marketingDelivery.testSend.restrictedNone")}
          </Alert>
        )}
        <TextField
          fullWidth
          type="email"
          autoComplete="off"
          label={translate("marketingDelivery.testSend.to")}
          value={to}
          onChange={handleTo}
        />
        <Stack direction="row" alignItems="center" gap={space.xs}>
          <Button type="submit" variant="outlined" disabled={!canSend}>
            {translate("marketingDelivery.testSend.send")}
          </Button>
        </Stack>
        {outcome && <Alert severity={outcome.severity}>{outcome.message}</Alert>}
      </SettingsSection>
    </Box>
  );
};
